/**
 * Production HTTP adapter for the generic PublicationEngine
 * repository contract.
 *
 * This module owns no publication state and performs no
 * publication business rules. It only translates repository
 * operations to the authoritative Studio Publication HTTP API.
 */
export function createStudioPublicationHttpRepository({
  fetchCurrentPublication,
  fetchPublication,
  listPublications: listPublicationsRequest,
  publishPublication: publishPublicationRequest,
  unpublishCurrentPublication,
} = {}) {
  assertFunction(
    fetchCurrentPublication,
    "fetchCurrentPublication"
  );

  assertFunction(
    fetchPublication,
    "fetchPublication"
  );

  assertFunction(
    listPublicationsRequest,
    "listPublications"
  );

  assertFunction(
    publishPublicationRequest,
    "publishPublication"
  );

  assertFunction(
    unpublishCurrentPublication,
    "unpublishCurrentPublication"
  );


  async function loadPublication(
    publicationId,
    context
  ) {
    try {
      const response =
        await fetchPublication(
          publicationId,
          extractScope(context)
        );

      return extractPublication(
        response
      );
    } catch (error) {
      if (isNotFound(error)) {
        return null;
      }

      throw error;
    }
  }


  async function loadCurrentPublication(
    documentId,
    context
  ) {
    try {
      const response =
        await fetchCurrentPublication(
          documentId,
          extractScope(context)
        );

      return extractPublication(
        response
      );
    } catch (error) {
      if (isNotFound(error)) {
        return null;
      }

      throw error;
    }
  }


  async function listPublications(
    documentId,
    context
  ) {
    const response =
      await listPublicationsRequest(
        documentId,
        extractScope(context)
      );

    return extractPublications(
      response
    );
  }


  async function publishPublication(
    record,
    _transition,
    context
  ) {
    assertPlainObject(
      record,
      "record"
    );

    const response =
      await publishPublicationRequest(
        normalizeRequiredString(
          record.documentId,
          "record.documentId"
        ),
        {
          versionId:
            normalizeRequiredString(
              record.versionId,
              "record.versionId"
            ),

          metadata:
            isPlainObject(
              record.metadata
            )
              ? record.metadata
              : {},
        },
        extractScope(context)
      );

    return extractPublication(
      response
    );
  }


  async function unpublishPublication(
    documentId,
    _transition,
    context
  ) {
    try {
      const response =
        await unpublishCurrentPublication(
          documentId,
          extractScope(context)
        );

      return extractPublication(
        response
      );
    } catch (error) {
      if (isNotFound(error)) {
        return null;
      }

      throw error;
    }
  }


  async function exists(
    publicationId,
    context
  ) {
    const publication =
      await loadPublication(
        publicationId,
        context
      );

    return publication !== null;
  }


  return Object.freeze({
    publishPublication,
    loadPublication,
    loadCurrentPublication,
    listPublications,
    unpublishPublication,
    exists,
  });
}


function extractScope(
  context
) {
  if (
    context === null
    || typeof context !== "object"
    || Array.isArray(context)
  ) {
    throw new TypeError(
      "Studio Publication HTTP repository requires a context object."
    );
  }

  return Object.freeze({
    companyUuid:
      normalizeRequiredString(
        context.companyUuid,
        "context.companyUuid"
      ),

    activityUuid:
      normalizeRequiredString(
        context.activityUuid,
        "context.activityUuid"
      ),
  });
}


function extractPublication(
  response
) {
  const body =
    extractResponseBody(
      response
    );

  const publication =
    body?.publication
    ?? null;

  if (
    publication === null
    || typeof publication !== "object"
    || Array.isArray(publication)
  ) {
    throw new TypeError(
      "Studio Publication API response must contain publication."
    );
  }

  return publication;
}


function extractPublications(
  response
) {
  const body =
    extractResponseBody(
      response
    );

  if (
    !Array.isArray(
      body?.publications
    )
  ) {
    throw new TypeError(
      "Studio Publication API response must contain publications[]."
    );
  }

  return body.publications;
}


function extractResponseBody(
  response
) {
  if (
    response
    && typeof response === "object"
    && !Array.isArray(response)
    && Object.prototype.hasOwnProperty.call(
      response,
      "data"
    )
  ) {
    return response.data;
  }

  return response;
}


function isNotFound(
  error
) {
  return (
    error?.response?.status === 404
    || error?.status === 404
  );
}


function assertFunction(
  value,
  name
) {
  if (typeof value !== "function") {
    throw new TypeError(
      `Studio Publication HTTP repository requires ${name}().`
    );
  }
}


function assertPlainObject(
  value,
  name
) {
  if (!isPlainObject(value)) {
    throw new TypeError(
      `${name} must be an object.`
    );
  }
}


function isPlainObject(
  value
) {
  return (
    value !== null
    && typeof value === "object"
    && !Array.isArray(value)
  );
}


function normalizeRequiredString(
  value,
  name
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new TypeError(
      `${name} must be a non-empty string.`
    );
  }

  return value.trim();
}


export default
  createStudioPublicationHttpRepository;
